/**
 * Port FRONTEND de la función renderTemplate del backend
 * (intervenciones/services/NotificacionService.js).
 *
 * Mismas reglas exactas:
 *   - {{var}} se sustituye por contexto.var (o '' si no existe)
 *   - {{#var}}...{{/var}} se mantiene si contexto.var es truthy, se quita si no
 *
 * IMPORTANTE: si el backend cambia su sintaxis (ej. añade helpers),
 * actualizar aquí. El preview del admin debe verse igual que lo que
 * el backend va a renderizar de verdad.
 */

export function renderTemplate(plantilla, contexto) {
  if (!plantilla) return '';

  // 1. Bloques condicionales {{#var}}...{{/var}}
  let resultado = plantilla.replace(
    /\{\{#(\w+)\}\}([\s\S]*?)\{\{\/\1\}\}/g,
    (_match, varName, content) => {
      const valor = contexto[varName];
      return valor ? content : '';
    }
  );

  // 2. Variables simples {{var}}
  resultado = resultado.replace(/\{\{(\w+)\}\}/g, (_match, varName) => {
    const valor = contexto[varName];
    if (valor === null || valor === undefined) return '';
    return String(valor);
  });

  return resultado;
}

/**
 * Catálogo de variables disponibles por tipo de plantilla.
 *
 * Si añades nuevos tipos al backend, actualiza aquí también.
 */
/* ══════════════════════════════════════════════════════════════════════════════════════════════
 * ESTE MAPA ES TRANSITORIO Y HAY QUE BORRARLO.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * El vocabulario de una plantilla lo declara ahora el SERVIDOR —`services/validarPlantilla.js`— y
 * viaja en el `GET /notificaciones/plantillas` dentro de `vocabulario`. `EditorPlantilla` lo
 * prefiere; este mapa solo se usa cuando el servidor todavia no declara ese tipo.
 *
 * ── LA MEDIDA DEL FINAL, PARA QUE NO SOBREVIVA POR INERCIA ────────────────────────────────────
 *
 * El servidor declara HOY **3 de los 24 tipos** —las tres de incorporaciones—. Cuando declare los 24, este mapa deja de usarse
 * **y hay que borrarlo**, junto con su rama en `EditorPlantilla`.
 *
 * Se dice con un numero y no con un «algun dia» a proposito: sin eso, un mapa sin uso sobrevive
 * anos —como el `bun.lock` que nadie puso— porque nada senala que sobra.
 *
 * Lo que falta por declarar son las 21 de intervenciones y soporte. Su vocabulario real vive en el
 * contexto que arma cada emisor, en CINCO ficheros y 19 sitios:
 *
 *     intervenciones/services/notificacionesHelper.js        9
 *     soporte/services/notificacionesSoporte.js              5
 *     intervenciones/controllers/Campanas_Controller.js      3
 *     intervenciones/services/recordatoriosSinCitaService.js 1
 *     intervenciones/services/NotificacionService.js         1
 *
 * Es trabajo mecanico de lectura, no dificil. Y NO se puede deducir del HTML que usan hoy: daria lo
 * que USA y no lo que PUEDE usar, una que no use ninguna saldria vacia, y cada borrado estrecharia
 * la lista para siempre.
 */
export const VARIABLES_POR_TIPO = {
  CITA_RESERVADA_USUARIO: [
    'usuario_nombre', 'tecnico_nombre', 'campana_titulo', 'campana_titulo_usuario',
    'cita_fecha', 'cita_hora', 'cita_hora_fin',
    'campana_fecha_limite', 'url_mis_citas',
  ],
  CITA_NUEVA_TECNICO: [
    'usuario_nombre', 'tecnico_nombre', 'campana_titulo',
    'cita_fecha', 'cita_hora', 'cita_hora_fin',
  ],
  CITA_CANCELADA_USUARIO_TECNICO: [
    'usuario_nombre', 'tecnico_nombre', 'campana_titulo',
    'cita_fecha', 'cita_hora',
  ],
  CITA_CANCELADA_ADMIN_USUARIO: [
    'usuario_nombre', 'tecnico_nombre', 'campana_titulo', 'campana_titulo_usuario',
    'cita_fecha', 'cita_hora', 'motivo',
    'campana_fecha_limite', 'url_mis_citas',
  ],
  CITA_NO_ASISTIO_USUARIO: [
    'usuario_nombre', 'campana_titulo_usuario',
    'cita_fecha', 'cita_hora', 'tecnico_nombre',
    'campana_fecha_limite', 'url_mis_citas',
  ],
  CITA_CANCELADA_ADMIN_TECNICO: [
    'usuario_nombre', 'tecnico_nombre', 'campana_titulo',
    'cita_fecha', 'cita_hora', 'motivo',
  ],
  CITA_RECORDATORIO_24H_USUARIO: [
    'usuario_nombre', 'tecnico_nombre', 'campana_titulo', 'campana_titulo_usuario',
    'cita_fecha', 'cita_hora',
    'campana_fecha_limite', 'url_mis_citas',
  ],
  CITA_RECORDATORIO_24H_TECNICO: [
    'usuario_nombre', 'tecnico_nombre', 'campana_titulo',
    'cita_fecha', 'cita_hora',
  ],
  CITA_TRASPASADA_TECNICO: [
    'tecnico_nombre', 'tecnico_origen_nombre', 'usuario_nombre',
    'campana_titulo', 'cita_fecha', 'cita_hora', 'url_panel_tecnico',
  ],
  USUARIO_NUEVA_CAMPANA: ['usuario_nombre', 'campana_titulo', 'campana_titulo_usuario', 'campana_fecha_limite', 'url_mis_citas'],
  CAMPANA_RECORDATORIO_SIN_CITA_USUARIO: [
    'usuario_nombre', 'campana_titulo_usuario',
    'campana_fecha_limite', 'url_mis_citas',
    'dias_restantes', 'dias_restantes_texto',
  ],
  ADMIN_RESUMEN_DIARIO: [],
};

/**
 * Valores de ejemplo para el preview.
 */
export const VARS_EJEMPLO = {
  usuario_nombre: 'María González',
  tecnico_nombre: 'Carlos Ruiz',
  campana_titulo: 'Renovación de equipos Q3',
  campana_titulo_usuario: 'Intervención técnica programada',
  cita_fecha: '15/07/2026',
  cita_hora: '10:00',
  cita_hora_fin: '10:30',
  motivo: 'Cambio de planificación',
  campana_fecha_limite: '25 de julio de 2026',
  url_mis_citas: 'https://intranet.grupolae.net/mis-citas',
  dias_restantes: 3,
  dias_restantes_texto: 'en 3 días laborables',
  tecnico_origen_nombre: 'Laura Sanz',
  url_panel_tecnico: 'https://intranet.grupolae.net/citas-tecnicas/panel-tecnico',
};

/**
 * Helper para descripción humana de cada variable (para tooltip/leyenda).
 */
export const DESCRIPCION_VARIABLE = {
  usuario_nombre: 'Nombre completo del usuario',
  tecnico_nombre: 'Nombre completo del técnico',
  campana_titulo: 'Título INTERNO de la campaña',
  campana_titulo_usuario: 'Título público de la campaña (con fallback al interno). Úsalo en plantillas para el usuario final.',
  cita_fecha: 'Fecha de la cita (DD/MM/YYYY)',
  cita_hora: 'Hora de inicio (HH:MM)',
  cita_hora_fin: 'Hora de fin (HH:MM)',
  motivo: 'Motivo (solo en cancelaciones admin)',
  campana_fecha_limite: 'Fecha límite para reservar (formato español)',
  url_mis_citas: 'Enlace directo a "Mis Citas" en la intranet',
  dias_restantes: 'Número de días laborables hasta la fecha límite (entero)',
  dias_restantes_texto: 'Frase amigable de días restantes ("hoy", "mañana", "en 3 días laborables")',
  tecnico_origen_nombre: 'Nombre del técnico que traspasa la cita',
  url_panel_tecnico: 'Enlace directo al Panel del técnico',
};
